import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Home as HomeIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  VerifiedUser as VerifiedIcon,
  Star as StarIcon,
  CheckCircle as CheckIcon,
  Phone as PhoneIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { BUSINESS, PAGE_META } from '../constants/business';

const Home: React.FC = () => {
  // House roof icon component for consistent branding
  const HouseIcon = ({ size = 24, color = '#1e3a8a' }) => (
    <HomeIcon sx={{ fontSize: size, color }} />
  );

  // Service data with house roof iconography
  const services = [
    {
      icon: <HouseIcon size={32} />,
      title: 'Residential Closings',
      description: 'Complete title services for home purchases and refinances',
      features: ['Title Search', 'Title Insurance', 'Closing Coordination'],
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 32, color: '#1e3a8a' }} />,
      title: 'Title Insurance',
      description: 'Comprehensive protection for your real estate investment',
      features: ['Owner\'s Policy', 'Lender\'s Policy', 'Extended Coverage'],
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 32, color: '#1e3a8a' }} />,
      title: 'Fast Closings',
      description: 'Streamlined process with quick turnaround times',
      features: ['24-48 Hour Commitment', 'Digital Processing', 'Flexible Scheduling'],
    },
  ];

  // Statistics with house roof accents
  const stats = [
    { number: '500+', label: 'Successful Closings', icon: <HouseIcon size={20} /> },
    { number: '10+', label: 'Years Experience', icon: <VerifiedIcon sx={{ fontSize: 20, color: '#f59e0b' }} /> },
    { number: '100%', label: 'Client Satisfaction', icon: <StarIcon sx={{ fontSize: 20, color: '#f59e0b' }} /> },
    { number: '48HR', label: 'Fast Turnaround', icon: <PhoneIcon sx={{ fontSize: 20, color: '#1e3a8a' }} /> },
  ];

  // Testimonials with clean, direct format
  const testimonials = [
    {
      name: 'Kristen Martin',
      role: 'Homebuyer',
      content: 'Professional, efficient, and trustworthy. MAD Title made our closing seamless.',
      rating: 5,
    },
    {
      name: 'Eric Caskey',
      role: 'Homebuyer',
      content: 'Reliable and always on top of everything. The whole process was smooth from start to finish.',
      rating: 5,
    },
    {
      name: 'Matthew Logan',
      role: 'Homeowner',
      content: 'Clear communication throughout the entire process. Highly recommended.',
      rating: 5,
    },
  ];

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": BUSINESS.legalName,
    "image": `${BUSINESS.siteUrl}${BUSINESS.ogImage}`,
    "@id": BUSINESS.siteUrl,
    "url": BUSINESS.siteUrl,
    "logo": `${BUSINESS.siteUrl}${BUSINESS.logo}`,
    "description": "Premier title insurance and real estate closing services in New Jersey. Expert title searches, settlement services, and comprehensive protection for your property investment.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": BUSINESS.address.street,
      "addressLocality": BUSINESS.address.city,
      "addressRegion": BUSINESS.address.state,
      "postalCode": BUSINESS.address.zip,
      "addressCountry": BUSINESS.address.country,
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": BUSINESS.geo.latitude,
      "longitude": BUSINESS.geo.longitude,
    },
    "telephone": BUSINESS.contact.phone,
    "email": BUSINESS.contact.email,
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "17:00",
      },
    ],
    "areaServed": [
      { "@type": "AdministrativeArea", "name": "Middlesex County, NJ" },
      { "@type": "State", "name": "New Jersey" },
    ],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How long does a typical title search take in New Jersey?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Most title searches in New Jersey take 24-48 hours to complete. However, complex properties with extensive ownership history or properties in certain municipalities may require additional time. We prioritize accuracy while maintaining fast turnaround times to meet your closing deadlines."
        }
      },
      {
        "@type": "Question",
        "name": "What's the difference between owner's and lender's title insurance?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Owner's title insurance protects your ownership rights and equity in the property for as long as you own it. Lender's title insurance protects only the mortgage lender's financial interest until the loan is paid off. We recommend both policies to ensure complete protection for all parties in the transaction."
        }
      },
      {
        "@type": "Question",
        "name": "Why do I need title insurance if the property has been owned for years?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Title insurance protects against defects that existed before you purchased the property, regardless of how long the previous owner held title. Issues like forged signatures, undisclosed heirs, recording errors, or liens from decades ago can still affect your ownership rights today."
        }
      },
      {
        "@type": "Question",
        "name": "How much does title insurance cost in New Jersey?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Title insurance premiums in New Jersey are regulated by the state and based on the property's purchase price. For an accurate quote on your specific transaction, contact us for a free estimate. We'll explain all costs upfront with no hidden fees or surprises at closing."
        }
      },
      {
        "@type": "Question",
        "name": "Can you handle closings for properties outside Middlesex County?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes! While we're based in Woodbridge, we provide title services throughout Eastern New Jersey, including Bergen, Essex, Union, Monmouth, and Warren counties. Our team is familiar with the recording requirements and procedures across multiple jurisdictions."
        }
      },
      {
        "@type": "Question",
        "name": "What happens if a title issue is discovered after closing?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "If you have title insurance and a covered defect is discovered after closing, your title insurance company will either resolve the issue, pay the legal costs to defend your title, or compensate you for any financial loss up to the policy amount. This is why proper title insurance is essential."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>{PAGE_META["/"].title}</title>
        <meta name="description" content={PAGE_META["/"].description} />
        <meta property="og:title" content={PAGE_META["/"].title} />
        <meta property="og:description" content={PAGE_META["/"].description} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={BUSINESS.displayName} />
        <meta property="og:image" content={`${BUSINESS.siteUrl}${BUSINESS.ogImage}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={`${BUSINESS.siteUrl}/`} />
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #3b82f6 100%)',
          color: 'white',
          py: { xs: 10, md: 14 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Geometric background pattern */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0.06,
            backgroundImage: `
              linear-gradient(30deg, transparent 40%, rgba(255,255,255,0.3) 40%, rgba(255,255,255,0.3) 42%, transparent 42%),
              linear-gradient(-30deg, transparent 40%, rgba(255,255,255,0.3) 40%, rgba(255,255,255,0.3) 42%, transparent 42%)
            `,
            backgroundSize: '80px 80px',
          }}
        />
        {/* Accent line */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #f59e0b 0%, #fbbf24 50%, #f59e0b 100%)',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
            <Typography
              variant="overline"
              sx={{
                color: '#fbbf24',
                fontSize: '0.875rem',
                letterSpacing: '0.15em',
                mb: 2,
                display: 'block',
              }}
            >
              {BUSINESS.displayName}
            </Typography>

            <Typography
              variant="h1"
              sx={{
                mb: 3,
                fontWeight: 800,
                fontSize: { xs: '1.875rem', md: '3.75rem' },
                lineHeight: 1.1,
                letterSpacing: '-0.01em',
              }}
            >
              Professional Title
              <br />
              & Closing Services
            </Typography>

            <Typography
              variant="h5"
              sx={{
                mb: 5,
                fontWeight: 400,
                opacity: 0.9,
                lineHeight: 1.5,
                maxWidth: 640,
                mx: 'auto',
                fontSize: { xs: '1.1rem', md: '1.25rem' },
              }}
            >
              Fast, secure, and reliable title and closing services
              in Woodbridge, Middlesex County, Warren County, and throughout Eastern New Jersey.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/contact"
                sx={{
                  backgroundColor: '#f59e0b',
                  color: '#1f2937',
                  px: 5,
                  py: 1.75,
                  fontSize: '1rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: '#d97706',
                    boxShadow: '0 4px 16px rgba(245, 158, 11, 0.4)',
                  },
                }}
              >
                GET FREE QUOTE
              </Button>

              <Button
                variant="outlined"
                size="large"
                component={Link}
                to="/services"
                sx={{
                  borderColor: 'rgba(255, 255, 255, 0.6)',
                  color: 'white',
                  borderWidth: 2,
                  px: 5,
                  py: 1.75,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  '&:hover': {
                    borderWidth: 2,
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                OUR SERVICES
              </Button>
            </Box>

            <Typography
              variant="body2"
              sx={{ mt: 4, opacity: 0.85, fontSize: '0.875rem' }}
            >
              Licensed by the New Jersey Department of Banking and Insurance
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Statistics Section */}
      <Box sx={{ py: 6, backgroundColor: '#f9fafb' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ mb: 2 }}>
                    {stat.icon}
                  </Box>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 800,
                      color: '#1f2937',
                      mb: 1,
                      fontSize: { xs: '1.5rem', md: '2rem' },
                    }}
                  >
                    {stat.number}
                  </Typography>
                  <Typography
                    variant="overline"
                    sx={{
                      color: '#6b7280',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      letterSpacing: '0.05em',
                      textTransform: 'none',
                    }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Services Section */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="overline"
              sx={{
                color: '#f59e0b',
                fontWeight: 600,
                letterSpacing: '0.1em',
                mb: 2,
                display: 'block',
              }}
            >
              OUR SERVICES
            </Typography>
            <Typography
              variant="h2"
              sx={{
                mb: 3,
                color: '#1e3a8a',
                fontWeight: 700,
              }}
            >
              Comprehensive
              <br />
              Title Solutions
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#64748b',
                maxWidth: '600px',
                mx: 'auto',
                fontSize: '1.125rem',
                lineHeight: 1.6,
              }}
            >
              Professional title and closing services designed to protect your investment
              and ensure smooth real estate transactions.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    border: '1px solid rgba(226, 232, 240, 0.8)',
                    borderTop: '4px solid #f59e0b',
                    boxShadow: '0 2px 8px rgba(30, 41, 59, 0.08)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(30, 41, 59, 0.12)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <Box
                      sx={{
                        mb: 3,
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(30, 58, 138, 0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                      }}
                    >
                      {service.icon}
                    </Box>

                    <Typography
                      variant="h5"
                      sx={{
                        mb: 2,
                        color: '#1e3a8a',
                        fontWeight: 700,
                        letterSpacing: '0.01em',
                      }}
                    >
                      {service.title}
                    </Typography>

                    <Typography
                      variant="body1"
                      sx={{
                        mb: 3,
                        color: '#4b5563',
                        lineHeight: 1.6,
                      }}
                    >
                      {service.description}
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
                      {service.features.map((feature, idx) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <CheckIcon sx={{ fontSize: 16, color: '#059669', mr: 1 }} />
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#1f2937',
                              fontWeight: 500,
                            }}
                          >
                            {feature}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    <Button
                      component={Link}
                      to="/services"
                      variant="text"
                      sx={{
                        color: '#1e3a8a',
                        fontWeight: 600,
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: 'rgba(30, 58, 138, 0.04)',
                        },
                      }}
                    >
                      Learn More &rarr;
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box sx={{ py: 8, backgroundColor: '#f9fafb' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="overline"
              sx={{
                color: '#f59e0b',
                fontWeight: 600,
                letterSpacing: '0.1em',
                mb: 2,
                display: 'block',
              }}
            >
              CLIENT TESTIMONIALS
            </Typography>
            <Typography
              variant="h2"
              sx={{
                mb: 3,
                color: '#1f2937',
                fontWeight: 700,
              }}
            >
              What Our
              <br />
              Clients Say
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Paper
                  sx={{
                    p: 4,
                    height: '100%',
                    borderRadius: 3,
                    border: '1px solid rgba(226, 232, 240, 0.8)',
                    boxShadow: '0 2px 8px rgba(30, 41, 59, 0.08)',
                  }}
                >
                  <Box sx={{ display: 'flex', mb: 2 }}>
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <StarIcon key={i} sx={{ fontSize: 20, color: '#d97706' }} />
                    ))}
                  </Box>
                  
                  <Typography
                    variant="body1"
                    sx={{
                      mb: 3,
                      color: '#1f2937',
                      lineHeight: 1.6,
                      fontStyle: 'italic',
                    }}
                  >
                    "{testimonial.content}"
                  </Typography>
                  
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 700,
                        color: '#1e3a8a',
                        letterSpacing: '0.02em',
                      }}
                    >
                      {testimonial.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#4b5563',
                      }}
                    >
                      {testimonial.role}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Why Title Insurance Matters Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              mb: 3,
              color: '#1f2937',
              fontWeight: 700,
            }}
          >
            Why Title Insurance is Essential in New Jersey
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: '#4b5563',
              maxWidth: 800,
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            Protecting your real estate investment with comprehensive title services across Middlesex County and Eastern New Jersey
          </Typography>
        </Box>

        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, color: '#1e3a8a', fontWeight: 600 }}>
              Understanding New Jersey Real Estate
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, color: '#374151' }}>
              New Jersey's complex property laws and rich historical record make title insurance more critical than ever. 
              With properties in Middlesex County dating back centuries, hidden liens, boundary disputes, and ownership 
              issues can emerge from historical records that impact your ownership rights.
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, color: '#374151' }}>
              Our comprehensive title search examines decades of public records, court documents, tax records, and 
              municipal filings to ensure your property has clear, marketable title. We identify and resolve issues 
              before they become costly problems, giving you peace of mind in your real estate investment.
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.7, color: '#374151' }}>
              From historic homes in Woodbridge Township to new developments in Edison, every property transaction 
              requires expert title services to protect against unforeseen title defects that could threaten your ownership.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 3,
              }}
            >
              <Typography variant="h5" component="h4" sx={{ mb: 3, color: '#1e3a8a', fontWeight: 600 }}>
                Common Title Issues We Prevent
              </Typography>
              <List>
                {[
                  'Undisclosed liens and judgments',
                  'Boundary line disputes and encroachments',
                  'Errors in public records and documentation',
                  'Fraudulent signatures on previous deeds',
                  'Unpaid property taxes and municipal assessments',
                  'Easement conflicts and right-of-way issues',
                  'Estate and probate complications',
                  'Construction and mechanics liens'
                ].map((issue, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={issue}
                      primaryTypographyProps={{ 
                        variant: 'body2',
                        sx: { fontSize: '1rem', color: '#374151' }
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Local Market Expertise Section */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              component="h2"
              sx={{
                mb: 3,
                color: '#1f2937',
                fontWeight: 700,
              }}
            >
              Expert Knowledge of New Jersey Markets
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: '#4b5563',
                maxWidth: 700,
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              Serving homebuyers, sellers, and real estate professionals throughout Middlesex County with specialized local expertise
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  p: 3,
                  textAlign: 'center',
                  border: '1px solid #e2e8f0',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 24px rgba(30, 58, 138, 0.12)',
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <Typography variant="h5" component="h3" sx={{ mb: 2, color: '#1e3a8a', fontWeight: 600 }}>
                  Woodbridge Township
                </Typography>
                <Typography variant="body1" paragraph sx={{ lineHeight: 1.6, color: '#374151' }}>
                  As our home base, we understand Woodbridge's unique property characteristics, from historic colonial homes 
                  to modern developments. Our team knows the local municipal requirements, tax considerations, and common 
                  title issues specific to this diverse township.
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', fontStyle: 'italic' }}>
                  Average closing time: 24-48 hours
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  p: 3,
                  textAlign: 'center',
                  border: '1px solid #e2e8f0',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 24px rgba(30, 58, 138, 0.12)',
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <Typography variant="h5" component="h3" sx={{ mb: 2, color: '#1e3a8a', fontWeight: 600 }}>
                  Edison & Middlesex County
                </Typography>
                <Typography variant="body1" paragraph sx={{ lineHeight: 1.6, color: '#374151' }}>
                  Extensive experience with Edison's rapid development and diverse housing stock. We handle everything from 
                  starter homes to luxury properties, understanding the nuances of each neighborhood's title requirements 
                  and potential complications.
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', fontStyle: 'italic' }}>
                  Specialized in new construction titles
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  p: 3,
                  textAlign: 'center',
                  border: '1px solid #e2e8f0',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 24px rgba(30, 58, 138, 0.12)',
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <Typography variant="h5" component="h3" sx={{ mb: 2, color: '#1e3a8a', fontWeight: 600 }}>
                  Eastern New Jersey
                </Typography>
                <Typography variant="body1" paragraph sx={{ lineHeight: 1.6, color: '#374151' }}>
                  From Bergen County to Monmouth County, our network and expertise extend throughout Eastern New Jersey. 
                  We understand regional variations in property law, municipal requirements, and recording procedures 
                  across multiple county jurisdictions.
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', fontStyle: 'italic' }}>
                  Multi-county expertise and coverage
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              mb: 3,
              color: '#1f2937',
              fontWeight: 700,
            }}
          >
            Frequently Asked Questions
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: '#4b5563',
              maxWidth: 600,
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            Common questions about title insurance and closing services in New Jersey
          </Typography>
        </Box>

        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          {[
            {
              question: "How long does a typical title search take in New Jersey?",
              answer: "Most title searches in New Jersey take 24-48 hours to complete. However, complex properties with extensive ownership history or properties in certain municipalities may require additional time. We prioritize accuracy while maintaining fast turnaround times to meet your closing deadlines."
            },
            {
              question: "What's the difference between owner's and lender's title insurance?",
              answer: "Owner's title insurance protects your ownership rights and equity in the property for as long as you own it. Lender's title insurance protects only the mortgage lender's financial interest until the loan is paid off. We recommend both policies to ensure complete protection for all parties in the transaction."
            },
            {
              question: "Why do I need title insurance if the property has been owned for years?",
              answer: "Title insurance protects against defects that existed before you purchased the property, regardless of how long the previous owner held title. Issues like forged signatures, undisclosed heirs, recording errors, or liens from decades ago can still affect your ownership rights today."
            },
            {
              question: "How much does title insurance cost in New Jersey?",
              answer: "Title insurance premiums in New Jersey are regulated by the state and based on the property's purchase price. For an accurate quote on your specific transaction, contact us for a free estimate. We'll explain all costs upfront with no hidden fees or surprises at closing."
            },
            {
              question: "Can you handle closings for properties outside Middlesex County?",
              answer: "Yes! While we're based in Woodbridge, we provide title services throughout Eastern New Jersey, including Bergen, Essex, Union, Monmouth, and Warren counties. Our team is familiar with the recording requirements and procedures across multiple jurisdictions."
            },
            {
              question: "What happens if a title issue is discovered after closing?",
              answer: "If you have title insurance and a covered defect is discovered after closing, your title insurance company will either resolve the issue, pay the legal costs to defend your title, or compensate you for any financial loss up to the policy amount. This is why proper title insurance is essential."
            }
          ].map((faq, index) => (
            <Accordion
              key={index}
              defaultExpanded={index === 0}
              sx={{
                mb: 1,
                border: '1px solid #e2e8f0',
                borderRadius: '8px !important',
                boxShadow: 'none',
                '&:before': { display: 'none' },
                '&.Mui-expanded': {
                  margin: '0 0 8px 0',
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: '#1e3a8a' }} />}
                sx={{
                  px: 3,
                  '&.Mui-expanded': { minHeight: 56 },
                  '& .MuiAccordionSummary-content.Mui-expanded': { my: 1.5 },
                }}
              >
                <Typography
                  variant="h6"
                  component="h3"
                  sx={{
                    color: '#1e3a8a',
                    fontWeight: 600,
                    fontSize: '1.1rem',
                  }}
                >
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, pb: 3 }}>
                <Typography
                  variant="body1"
                  sx={{
                    lineHeight: 1.7,
                    color: '#374151',
                  }}
                >
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Container>

      {/* CTA Section */}
      <Box
        sx={{
          py: 8,
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <HouseIcon size={48} color="white" />
          
          <Typography
            variant="h2"
            sx={{
              mb: 3,
              mt: 3,
              fontWeight: 700,
            }}
          >
            Ready to Get Started?
          </Typography>
          
          <Typography
            variant="h5"
            sx={{
              mb: 4,
              opacity: 0.9,
              fontWeight: 400,
            }}
          >
            Contact us today for a free consultation and quote on your New Jersey title services.
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              component={Link}
              to="/contact"
              sx={{
                backgroundColor: '#f59e0b',
                color: '#1f2937',
                px: 6,
                py: 2,
                fontSize: '1.125rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#d97706',
                  boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
                },
              }}
            >
              GET FREE QUOTE NOW
            </Button>
            <Button
              variant="outlined"
              size="large"
              href="tel:732-874-5425"
              sx={{
                borderColor: 'white',
                color: 'white',
                px: 4,
                py: 2,
                fontSize: '1.125rem',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderColor: 'white',
                },
              }}
            >
              CALL (732) 874-5425
            </Button>
          </Box>
        </Container>
      </Box>

    </>
  );
};

export default Home; 